import type { ReactNode } from "react";

import { ToggleButton, ToggleButtonGroup } from "@mui/material";

import { cn } from "../utils/styling";
import { Icon, type IconName } from "./Icon";

export type ToggleOption<Value extends string> = {
  iconName?: IconName;
  label: ReactNode;
  value: Value;
};

type ToggleProps<Value extends string> = {
  className?: string;
  disabled?: boolean;
  onChange: (value: Value) => void;
  options: ToggleOption<Value>[];
  value: Value;
};

const buttonClassName = cn(
  "h-ds-2x min-w-0 gap-1 !rounded-none px-3 normal-case !font-ds-body-regular-xs",
  "bg-lds-surface-bright text-lds-foreground-primary border-ds-strong",
  "hover:bg-lds-selection-strong hover:text-lds-foreground-heading-active",
  "[&.Mui-selected]:bg-lds-selection-subtle [&.Mui-selected]:text-lds-foreground-heading-active [&.Mui-selected]:border-ds-accent",
  "first:!rounded-l-ds-0-25x last:!rounded-r-ds-0-25x"
);

export const Toggle = <Value extends string>({ className, disabled, onChange, options, value }: ToggleProps<Value>) => (
  <ToggleButtonGroup
    className={cn(
      "flex min-w-0 gap-0",
      "[&_.MuiToggleButtonGroup-grouped]:!mx-0 [&_.MuiToggleButtonGroup-grouped:not(:first-of-type)]:-ml-px",
      className
    )}
    disabled={disabled}
    exclusive
    onChange={(_, next) => {
      const match = options.find((option) => option.value === next);
      if (match) onChange(match.value);
    }}
    value={value}
  >
    {options.map((option) => (
      <ToggleButton className={buttonClassName} key={option.value} value={option.value}>
        {option.iconName ? <Icon iconName={option.iconName} size="sm" /> : null}
        <span>{option.label}</span>
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);
